import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { School, GraduationCap } from "lucide-react";
import { PdfViewerModal } from "@/components/PdfViewerModal";

// Färgpalett från SettingsPage — duplicerad här för att hålla komponenten självständig.
const COLORS = ["#E07A5F", "#97BC62", "#F6CA83", "#82A0BC", "#3D5A80", "#475F61"];

/**
 * Innehåller alla 6 dialoger som tidigare låg inline i SettingsPage:
 * 1. Student-credentials dialog (Vklass elev-inlogg per feed)
 * 2. Vklass-konto dialog (förälder + elev unified)
 * 3. Värmdö Classroom-konto dialog
 * 4. iCal-feed create/edit dialog
 * 5. PdfViewer för attachment preview
 * 6. Ny familjemedlem-dialog
 *
 * Tar emot ALL state via `dlg`-prop och anrop via samma objekt. UI-only refaktor —
 * ingen logik flyttad. Hade legat i samma kontext om JS-modulerna var avgränsade.
 */
export function SettingsDialogs({ dlg }) {
  const {
    // student creds
    studentOpen, setStudentOpen, studentForm, setStudentForm, studentBusy, saveStudentCredentials,
    // vklass
    vklassOpen, setVklassOpen, editingVklass, setEditingVklass, vklassForm, setVklassForm,
    vklassBusy, connectVklass,
    // varmdo
    varmdoOpen, setVarmdoOpen, editingVarmdo, setEditingVarmdo, varmdoForm, setVarmdoForm,
    varmdoBusy, connectVarmdo,
    // iCal feed
    open, setOpen, editing, form, setForm, save,
    // attachment preview
    attachmentPreview, setAttachmentPreview,
    // new member
    newMemberOpen, setNewMemberOpen, newMember, setNewMember, creatingMember, createNewMember,
  } = dlg;

  return (
    <>
      {/* Student-credentials (Vklass elev-inlogg per feed) */}
      <Dialog open={studentOpen} onOpenChange={setStudentOpen}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="student-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <School size={18} className="text-[color:var(--moss)]" />
              Elev-inlogg{studentForm.owner ? ` · ${studentForm.owner}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-[color:var(--text-secondary)]">
              Vklass elevkonto för {studentForm.owner || "barnet"}.
              Vi loggar in var 4:e timme för att hämta fullständiga uppgiftsbeskrivningar.
              Lösenordet lagras <span className="font-semibold">krypterat</span> (Fernet/AES) i databasen.
            </p>
            <div>
              <label className="label-eyebrow block mb-1.5">Användarnamn</label>
              <Input
                value={studentForm.username}
                onChange={(e) => setStudentForm({ ...studentForm, username: e.target.value })}
                placeholder="t.ex. ANTO123456S"
                className="rounded-xl font-mono text-sm"
                data-testid="student-username-input"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Lösenord</label>
              <Input
                type="password"
                value={studentForm.password}
                onChange={(e) => setStudentForm({ ...studentForm, password: e.target.value })}
                placeholder="Vklass elev-lösenord"
                className="rounded-xl"
                data-testid="student-password-input"
                autoComplete="off"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setStudentOpen(false)} data-testid="student-cancel-button">
              Avbryt
            </Button>
            <Button
              onClick={saveStudentCredentials}
              disabled={studentBusy}
              className="bg-[color:var(--moss)] hover:bg-[#1f421f]"
              data-testid="student-save-button"
            >
              {studentBusy ? "Verifierar..." : "Spara"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vklass-konto (förälder + elev unified) */}
      <Dialog open={vklassOpen} onOpenChange={(o) => { if (!o) { setVklassOpen(false); setEditingVklass(null); } }}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="vklass-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <School size={18} className="text-[color:var(--moss)]" />
              {editingVklass
                ? `Redigera ${editingVklass.kind === "parent" ? "förälder" : "elev"} · ${editingVklass.label}`
                : "Anslut Vklass (förälder)"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-[color:var(--text-secondary)]">
              {editingVklass
                ? "Lämna lösenordet tomt för att behålla det sparade. Skriv in nytt lösenord för att uppdatera + synka direkt."
                : "Logga in en gång (förälder/vårdnadshavare) så hämtas barnens schema, läxor och prov automatiskt. Inloggningen sparas krypterat (AES) och nattjobb kl 03:15 upptäcker nya barn."}
            </p>
            {(!editingVklass || editingVklass.kind === "parent") && (
              <div>
                <label className="label-eyebrow block mb-1.5">
                  Label {editingVklass ? <span className="font-normal text-[color:var(--text-tertiary)]">(synligt i appen)</span> : "(valfritt)"}
                </label>
                <Input
                  value={vklassForm.label}
                  onChange={(e) => setVklassForm({ ...vklassForm, label: e.target.value })}
                  placeholder="t.ex. Emil (förälder)"
                  className="rounded-xl text-sm"
                  data-testid="vklass-label-input"
                />
              </div>
            )}
            <div>
              <label className="label-eyebrow block mb-1.5">Användarnamn</label>
              <Input
                value={vklassForm.username}
                onChange={(e) => setVklassForm({ ...vklassForm, username: e.target.value })}
                placeholder="Vklass användarnamn"
                className="rounded-xl font-mono text-sm"
                data-testid="vklass-username-input"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">
                Lösenord {editingVklass && <span className="text-[color:var(--text-tertiary)] font-normal">(lämna tomt = behåll)</span>}
              </label>
              <Input
                type="password"
                value={vklassForm.password}
                onChange={(e) => setVklassForm({ ...vklassForm, password: e.target.value })}
                placeholder={editingVklass ? "Lämna tomt för att behålla" : "Vklass-lösenord"}
                className="rounded-xl"
                data-testid="vklass-password-input"
                autoComplete="off"
              />
            </div>
            <p className="text-[10px] text-[color:var(--text-tertiary)]">
              Tips: Om du nyligen testat flera gånger kan Vklass tillfälligt blockera kontot i ~30 min.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => { setVklassOpen(false); setEditingVklass(null); }}
              data-testid="vklass-cancel-button"
            >
              Avbryt
            </Button>
            <Button
              onClick={connectVklass}
              disabled={vklassBusy}
              className="bg-[color:var(--moss)] hover:bg-[#1f421f]"
              data-testid="vklass-connect-button"
            >
              {vklassBusy ? "Arbetar..." : editingVklass ? "Spara" : "Anslut"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Värmdö Classroom-konto */}
      <Dialog open={varmdoOpen} onOpenChange={(o) => { if (!o) { setVarmdoOpen(false); setEditingVarmdo(null); } }}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="varmdo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <GraduationCap size={18} className="text-[color:var(--moss)]" />
              {editingVarmdo ? `Redigera ${editingVarmdo.label}` : "Anslut Värmdö Classroom"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-[color:var(--text-secondary)]">
              {editingVarmdo
                ? "Lämna lösenordet tomt för att behålla det sparade. Skriv in nytt lösenord för att uppdatera + synka direkt."
                : "Eftersom Värmdös skola blockerar tredjepartsappar loggar appen in som eleven via login.varmdo.se och skrapar Classroom direkt. Inloggningen sparas krypterat (AES) och uppdateras var 4:e timme."}
            </p>
            <div>
              <label className="label-eyebrow block mb-1.5">Namn / Label (synligt i appen)</label>
              <Input
                value={varmdoForm.label}
                onChange={(e) => setVarmdoForm({ ...varmdoForm, label: e.target.value })}
                placeholder="Anton"
                className="rounded-xl text-sm"
                data-testid="varmdo-label-input"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Skol-e-post (för Google)</label>
              <Input
                value={varmdoForm.email}
                onChange={(e) => setVarmdoForm({ ...varmdoForm, email: e.target.value })}
                placeholder="anton.jonasson@edu.varmdo.se"
                className="rounded-xl font-mono text-sm"
                data-testid="varmdo-email-input"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Värmdö-portal användarnamn</label>
              <Input
                value={varmdoForm.username}
                onChange={(e) => setVarmdoForm({ ...varmdoForm, username: e.target.value })}
                placeholder="antjon0121"
                className="rounded-xl font-mono text-sm"
                data-testid="varmdo-username-input"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">
                Värmdö-lösenord {editingVarmdo && <span className="text-[color:var(--text-tertiary)] font-normal">(lämna tomt = behåll)</span>}
              </label>
              <Input
                type="password"
                value={varmdoForm.password}
                onChange={(e) => setVarmdoForm({ ...varmdoForm, password: e.target.value })}
                placeholder={editingVarmdo ? "Lämna tomt för att behålla" : "Lösenord"}
                className="rounded-xl"
                data-testid="varmdo-password-input"
                autoComplete="off"
              />
            </div>
            <p className="text-[10px] text-[color:var(--text-tertiary)]">
              Första anslutningen tar ~20–40 sekunder eftersom appen loggar in och hämtar alla kurser.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setVarmdoOpen(false); setEditingVarmdo(null); }} data-testid="varmdo-cancel-button">
              Avbryt
            </Button>
            <Button
              onClick={connectVarmdo}
              disabled={varmdoBusy}
              className="bg-[color:var(--moss)] hover:bg-[#1f421f]"
              data-testid="varmdo-connect-button"
            >
              {varmdoBusy ? "Arbetar..." : editingVarmdo ? "Spara" : "Anslut"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iCal-feed skapa/redigera */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="feed-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Redigera kalender" : "Ny iCal-kalender"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="label-eyebrow block mb-1.5">Namn</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="t.ex. Skolschemat"
                className="rounded-xl"
                data-testid="feed-name-input"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Ägare/Barn</label>
              <Input
                value={form.owner}
                onChange={(e) => setForm({ ...form, owner: e.target.value })}
                placeholder="t.ex. Maja"
                className="rounded-xl"
                data-testid="feed-owner-input"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">iCal URL</label>
              <Input
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://... eller webcal://..."
                className="rounded-xl font-mono text-xs"
                data-testid="feed-url-input"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Färg</label>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setForm({ ...form, color: c })}
                    className={`w-9 h-9 rounded-full ${
                      form.color === c ? "ring-2 ring-offset-2 ring-[color:var(--moss)]" : ""
                    }`}
                    style={{ backgroundColor: c }}
                    data-testid={`feed-color-${c}`}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="feed-cancel-button">
              Avbryt
            </Button>
            <Button
              onClick={save}
              className="bg-[color:var(--moss)] hover:bg-[#1f421f]"
              data-testid="feed-save-button"
            >
              Spara
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attachment preview (PDF/bild) */}
      {attachmentPreview && (
        <PdfViewerModal
          url={attachmentPreview.url}
          title={attachmentPreview.title}
          mime={attachmentPreview.mime}
          onClose={() => setAttachmentPreview(null)}
        />
      )}

      {/* Ny familjemedlem */}
      <Dialog open={newMemberOpen} onOpenChange={setNewMemberOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Lägg till familjemedlem</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-medium text-[color:var(--text-secondary)] mb-1 block">Namn</label>
              <Input
                placeholder="Anna Andersson"
                value={newMember.name}
                onChange={(e) => setNewMember((m) => ({ ...m, name: e.target.value }))}
                data-testid="new-member-name"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[color:var(--text-secondary)] mb-1 block">E-post</label>
              <Input
                type="email"
                placeholder="anna@example.com"
                value={newMember.email}
                onChange={(e) => setNewMember((m) => ({ ...m, email: e.target.value }))}
                data-testid="new-member-email"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[color:var(--text-secondary)] mb-1 block">Mobil (valfritt)</label>
              <Input
                placeholder="+46701234567"
                value={newMember.phone}
                onChange={(e) => setNewMember((m) => ({ ...m, phone: e.target.value }))}
                data-testid="new-member-phone"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[color:var(--text-secondary)] mb-1 block">
                Extra-mejl (iCloud, kommaseparerade, valfritt)
              </label>
              <Input
                placeholder="anna@icloud.com"
                value={newMember.extra_emails}
                onChange={(e) => setNewMember((m) => ({ ...m, extra_emails: e.target.value }))}
                data-testid="new-member-extra-emails"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[color:var(--text-secondary)] mb-1 block">Färg</label>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setNewMember((m) => ({ ...m, avatar_color: c }))}
                    className={`w-7 h-7 rounded-full border-2 ${
                      newMember.avatar_color === c ? "border-[color:var(--text-primary)]" : "border-transparent"
                    }`}
                    style={{ backgroundColor: c }}
                    data-testid={`new-member-color-${c}`}
                  />
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer pt-1">
              <input
                type="checkbox"
                checked={newMember.is_child}
                onChange={(e) => setNewMember((m) => ({ ...m, is_child: e.target.checked }))}
                data-testid="new-member-is-child"
              />
              <span>Det här är ett barn (ser bara egen data)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewMemberOpen(false)} data-testid="new-member-cancel">
              Avbryt
            </Button>
            <Button
              onClick={createNewMember}
              disabled={creatingMember}
              className="bg-[color:var(--moss)] hover:bg-[#1f421f]"
              data-testid="new-member-save"
            >
              {creatingMember ? "Skapar…" : "Lägg till"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
